import type { IconName } from './icons'

/*
  The navigation (owner, 2026-10-23: direction B, "Ranch office"). The top bar names five
  sections; the row under it lists the open section's pages. The vocabulary is the style
  guide's (brief §2.7.1, §2.7.4): Cattle, Pastures, Records, not "Herd module", "Land
  units", "Documents". Routes per docs/architecture.md §7.

  Settings, About and Admin sit at the right of the top bar, not in a section.
*/
export interface NavItem {
  to: string
  label: string
  icon: IconName
  end?: boolean
  /** Shown only to a platform-admin. */
  platformAdmin?: boolean
}

export interface NavSection {
  key: string
  label: string
  /** Where the section's top-bar link goes: its first page. */
  to: string
  end?: boolean
  /** The pages in the section's row; none for Home. */
  items: NavItem[]
}

export const SECTIONS: NavSection[] = [
  { key: 'home', label: 'Home', to: '/', end: true, items: [] },
  {
    key: 'herd',
    label: 'Herd',
    to: '/cattle',
    items: [
      { to: '/cattle', label: 'Cattle', icon: 'cattle', end: true },
      { to: '/cattle/missing', label: 'Missing', icon: 'warning' },
      { to: '/cattle/chute', label: 'Chute', icon: 'cattle' },
      { to: '/cattle/calving', label: 'Calving', icon: 'cattle' },
      { to: '/cattle/move', label: 'Move a herd', icon: 'cattle' },
      { to: '/cattle/uploads', label: 'Uploads', icon: 'records' },
      { to: '/cattle/breeding', label: 'Breeding', icon: 'cattle' },
      { to: '/cattle/marketing', label: 'Marketing', icon: 'cattle' },
    ],
  },
  {
    key: 'land',
    label: 'Land',
    to: '/pastures',
    items: [
      { to: '/pastures', label: 'Pastures', icon: 'pastures' },
      { to: '/hay', label: 'Hay', icon: 'hay' },
      { to: '/map', label: 'Map', icon: 'map' },
      { to: '/leases', label: 'Leases', icon: 'leases' },
    ],
  },
  {
    key: 'work',
    label: 'Work',
    to: '/tasks',
    items: [
      { to: '/tasks', label: 'Tasks', icon: 'tasks' },
      { to: '/notes', label: 'Notes', icon: 'notes' },
      { to: '/questions', label: 'Questions', icon: 'about' },
    ],
  },
  {
    key: 'books',
    label: 'Books',
    to: '/records',
    items: [
      { to: '/records', label: 'Records', icon: 'records' },
      { to: '/finance', label: 'Finance', icon: 'records' },
      { to: '/reports', label: 'Reports', icon: 'reports' },
    ],
  },
]

/** The right of the top bar. */
export const UTILITY_ITEMS: NavItem[] = [
  { to: '/settings', label: 'Settings', icon: 'settings' },
  // Platform-admin only (owner, 2026-10-22): setting up a ranch is the site's work.
  { to: '/admin', label: 'Admin', icon: 'lock', platformAdmin: true },
]

/** Every page a person can reach from the navigation, for tests and the screenshot run. */
export const SIDEBAR_ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  ...SECTIONS.flatMap((s) => s.items),
  ...UTILITY_ITEMS,
]

/** The section a path belongs to: the one whose pages include it, else Home. */
export function sectionOf(pathname: string): NavSection {
  const match = (to: string) => pathname === to || pathname.startsWith(`${to}/`)
  return (
    SECTIONS.find((s) => s.items.some((i) => match(i.to))) ??
    SECTIONS.find((s) => s.key === 'home')!
  )
}

/** The items this person may see: Admin only for a platform-admin. */
export function visibleItems(items: NavItem[], platformAdmin: boolean): NavItem[] {
  return items.filter((item) => !item.platformAdmin || platformAdmin)
}
